import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MessageSquare,
  Users,
  Search,
  Plus,
  X,
  Check,
  Phone,
  Video,
  UserPlus,
  ArrowUp,
  ArrowLeft,
  AlertTriangle
} from 'lucide-react';
import type { DirectConversation, WorkspaceMember } from '@sahaj/types';
import { useChat } from '../../context/ChatContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useCall } from '../../context/CallContext';
import { useEmergency } from '../../context/EmergencyContext';
import { useLanguage } from '../../hooks/useLanguage';
import { MessageItem } from '../chat/MessageItem';
import { MessageComposer } from '../chat/MessageComposer';
import { UserAvatar } from '../common/UserAvatar';
import { api } from '../../lib/api';

/** Time threshold (ms) to break a message group even from the same sender */
const GROUP_BREAK_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

interface DmHubProps {
  onBack?: () => void;
}

export const DmHub: React.FC<DmHubProps> = ({ onBack }) => {
  const {
    directConversations,
    activeChat,
    messages,
    isLoadingMessages,
    hasMoreMessages,
    loadOlderMessages,
    sendMessage,
    selectDm,
    createDm
  } = useChat();

  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const { initiateCall } = useCall();
  const { openSendModal } = useEmergency();
  const { t } = useLanguage();

  const [searchQuery, setSearchQuery] = useState('');
  const [isNewDmModalOpen, setIsNewDmModalOpen] = useState(false);
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [groupTitle, setGroupTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef<boolean>(true);
  const prevChatIdRef = useRef<string | null>(null);
  const prevMessagesCountRef = useRef<number>(0);

  // Fetch workspace members for search and starting new DMs
  useEffect(() => {
    if (!currentWorkspace) return;
    api.workspaces.listMembers(currentWorkspace.id)
      .then(members => {
        setWorkspaceMembers(members.filter(m => m.user.id !== user?.id));
      })
      .catch(err => console.error('Failed to fetch workspace members', err));
  }, [currentWorkspace, user?.id]);

  // Current active conversation if activeChat is of type 'dm'
  const activeConversation: DirectConversation | null = useMemo(() => {
    if (activeChat?.type === 'dm') {
      return activeChat.conversation;
    }
    return null;
  }, [activeChat]);

  // Filtered direct conversations
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return directConversations;
    const q = searchQuery.toLowerCase();
    return directConversations.filter(conv => {
      if (conv.title && conv.title.toLowerCase().includes(q)) return true;
      const otherMembers = conv.members.filter(m => m.id !== user?.id);
      return otherMembers.some(m => m.displayName.toLowerCase().includes(q));
    });
  }, [directConversations, searchQuery, user?.id]);

  // Workspace members matching search query who don't have an existing 1-on-1 DM
  const membersWithoutDm = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    const existing1on1UserIds = new Set<string>();
    directConversations.forEach(c => {
      const others = c.members.filter(m => m.id !== user?.id);
      if (others.length === 1 && others[0]) {
        existing1on1UserIds.add(others[0].id);
      }
    });

    return workspaceMembers.filter(
      m => !existing1on1UserIds.has(m.user.id) && m.user.displayName.toLowerCase().includes(q)
    );
  }, [searchQuery, workspaceMembers, directConversations, user?.id]);

  // Modal filtered members
  const modalFilteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return workspaceMembers;
    const q = memberSearch.toLowerCase();
    return workspaceMembers.filter(m => m.user.displayName.toLowerCase().includes(q));
  }, [workspaceMembers, memberSearch]);

  // Instant scroll to bottom
  const scrollToBottomInstant = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
      isNearBottomRef.current = true;
    }
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const threshold = 120;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distanceFromBottom < threshold;

    if (el.scrollTop < 60 && hasMoreMessages && !isLoadingMessages) {
      const prevScrollHeight = el.scrollHeight;
      loadOlderMessages().then(() => {
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            const diff = scrollContainerRef.current.scrollHeight - prevScrollHeight;
            scrollContainerRef.current.scrollTop += diff;
          }
        });
      });
    }
  }, [hasMoreMessages, isLoadingMessages, loadOlderMessages]);

  const activeDmId = activeConversation?.id || null;

  // Auto scroll on conversation change and new messages
  useEffect(() => {
    const isChatChanged = activeDmId !== prevChatIdRef.current;
    prevChatIdRef.current = activeDmId;

    if (isChatChanged) {
      prevMessagesCountRef.current = messages.length;
      isNearBottomRef.current = true;
      requestAnimationFrame(() => {
        scrollToBottomInstant();
        setTimeout(scrollToBottomInstant, 50);
      });
      return;
    }

    const newMessagesCount = messages.length;
    const hasNewIncoming = newMessagesCount > prevMessagesCountRef.current;
    prevMessagesCountRef.current = newMessagesCount;

    if (hasNewIncoming && isNearBottomRef.current) {
      requestAnimationFrame(() => {
        scrollToBottomInstant();
      });
    }
  }, [activeDmId, messages.length, scrollToBottomInstant]);

  const handleSelectDmItem = (conversation: DirectConversation) => {
    selectDm(conversation);
  };

  const handleStartDmWithMember = async (member: WorkspaceMember) => {
    try {
      setIsSubmitting(true);
      const conv = await createDm({ participantId: member.user.id });
      selectDm(conv);
      setSearchQuery('');
    } catch (err) {
      console.error('Failed to create DM conversation', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleMemberModal = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleCreateDmModal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUserIds.length === 0 || isSubmitting) return;

    try {
      setIsSubmitting(true);
      let conv: DirectConversation;
      if (selectedUserIds.length === 1 && selectedUserIds[0]) {
        conv = await createDm({ participantId: selectedUserIds[0] });
      } else {
        conv = await createDm({
          participantIds: selectedUserIds,
          ...(groupTitle.trim() ? { title: groupTitle.trim() } : {})
        });
      }
      setIsNewDmModalOpen(false);
      setSelectedUserIds([]);
      setGroupTitle('');
      selectDm(conv);
    } catch (err) {
      console.error('Failed to create DM conversation', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTimestamp = (isoString?: string) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getConversationTitle = (conversation: DirectConversation) => {
    if (conversation.title) return conversation.title;
    const otherMembers = conversation.members.filter(m => m.id !== user?.id);
    if (otherMembers.length === 0) return 'Direct Message';
    return otherMembers.map(m => m.displayName).join(', ');
  };

  const getConversationSubtitle = (conversation: DirectConversation) => {
    if (conversation.lastMessage) {
      return `${conversation.lastMessage.senderDisplayName}: ${conversation.lastMessage.content}`;
    }
    const otherMembers = conversation.members.filter(m => m.id !== user?.id);
    if (otherMembers.length > 1) {
      return `${otherMembers.length + 1} members`;
    }
    return 'No messages yet. Say hello!';
  };

  // Compute message grouping metadata
  const getGroupingInfo = (index: number) => {
    const msg = messages[index];
    if (!msg) {
      return { isFirstInGroup: true, isLastInGroup: true };
    }

    const prevMsg = index > 0 ? messages[index - 1] : undefined;
    const nextMsg = index < messages.length - 1 ? messages[index + 1] : undefined;

    const isSameSenderAsPrev =
      !!prevMsg &&
      prevMsg.sender?.id === msg.sender?.id &&
      !prevMsg.deletedAt &&
      !msg.deletedAt &&
      new Date(msg.createdAt).getTime() - new Date(prevMsg.createdAt).getTime() < GROUP_BREAK_THRESHOLD_MS;

    const isSameSenderAsNext =
      !!nextMsg &&
      nextMsg.sender?.id === msg.sender?.id &&
      !nextMsg.deletedAt &&
      !msg.deletedAt &&
      new Date(nextMsg.createdAt).getTime() - new Date(msg.createdAt).getTime() < GROUP_BREAK_THRESHOLD_MS;

    return {
      isFirstInGroup: !isSameSenderAsPrev,
      isLastInGroup: !isSameSenderAsNext,
    };
  };

  // Active chat contact info
  const otherMembersActive = activeConversation
    ? activeConversation.members.filter(m => m.id !== user?.id)
    : [];
  const isGroupActive = otherMembersActive.length > 1;
  const firstOtherActive = otherMembersActive[0];
  const activeTitle = activeConversation ? getConversationTitle(activeConversation) : '';

  return (
    <div className="flex-1 flex h-full bg-background text-foreground overflow-hidden">
      {/* ====================================================================
          LEFT COLUMN: Conversations & Contacts List (WhatsApp Web Style)
          ==================================================================== */}
      <aside className="w-80 sm:w-96 flex-shrink-0 border-r border-border flex flex-col h-full bg-card/30 select-none">
        {/* Left Pane Header */}
        <div className="h-16 px-4 border-b border-border flex items-center justify-between gap-3 bg-card/40 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                title="Back to Channels"
                aria-label="Back to Channels"
                className="p-1.5 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition flex items-center gap-1 flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 shadow-xs">
              <MessageSquare className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold text-foreground text-sm truncate flex items-center gap-2">
                <span>{t('dm.title')}</span>
                {directConversations.length > 0 && (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                    {directConversations.length}
                  </span>
                )}
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setIsNewDmModalOpen(true)}
            title={t('dm.newDmModalTitle')}
            aria-label={t('dm.newDmModalTitle')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-primary-foreground font-semibold text-xs hover:bg-primary/90 transition shadow-xs"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>{t('dm.newChat')}</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-3 border-b border-border bg-card/20 flex-shrink-0">
          <div className="relative flex items-center bg-muted/70 rounded-xl px-3 py-1.5 border border-border/60 focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/10 transition">
            <Search className="h-3.5 w-3.5 text-muted-foreground mr-2 flex-shrink-0" />
            <input
              type="text"
              placeholder={t('dm.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label={t('dm.searchPlaceholder')}
              className="bg-transparent border-none focus:outline-none text-xs w-full placeholder:text-muted-foreground text-foreground"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-muted-foreground hover:text-foreground ml-1"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredConversations.length === 0 && membersWithoutDm.length === 0 ? (
            <div className="py-12 px-4 text-center">
              <div className="w-12 h-12 rounded-2xl bg-muted/60 text-muted-foreground flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="h-6 w-6" />
              </div>
              <p className="text-xs font-semibold text-foreground">
                {searchQuery ? t('dm.noConversationsFound') : t('dm.noConversationsFound')}
              </p>
              <p className="text-[11px] text-muted-foreground mt-1 max-w-[200px] mx-auto">
                {searchQuery
                  ? t('dm.noConversationsDesc')
                  : t('dm.noConversationsDesc')}
              </p>
              <button
                type="button"
                onClick={() => setIsNewDmModalOpen(true)}
                className="mt-3.5 inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground font-semibold text-xs rounded-xl shadow hover:bg-primary/90 transition"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>{t('dm.startConversation')}</span>
              </button>
            </div>
          ) : (
            <>
              {/* Active / Existing Conversations */}
              {filteredConversations.map((conv) => {
                const otherMembers = conv.members.filter(m => m.id !== user?.id);
                const isGroup = otherMembers.length > 1;
                const isSelected = activeConversation?.id === conv.id;
                const hasUnread = (conv.unreadCount || 0) > 0;
                const firstOther = otherMembers[0];
                const title = getConversationTitle(conv);

                return (
                  <div
                    key={conv.id}
                    onClick={() => handleSelectDmItem(conv)}
                    className={`group relative p-2.5 rounded-xl border transition-all cursor-pointer flex items-center gap-3 ${
                      isSelected
                        ? 'bg-primary/10 border-primary/40 shadow-xs text-foreground ring-1 ring-primary/20'
                        : hasUnread
                        ? 'bg-card border-primary/30 hover:bg-card/90 shadow-xs'
                        : 'bg-transparent border-transparent hover:bg-muted/50 hover:border-border/60'
                    }`}
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      {isGroup ? (
                        <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary border border-primary/30 flex items-center justify-center font-bold text-xs shadow-xs">
                          <Users className="h-5 w-5" />
                        </div>
                      ) : (
                        <UserAvatar
                          userId={firstOther?.id}
                          displayName={title}
                          avatarUrl={firstOther?.avatarUrl}
                          size="md"
                          showStatus
                          className="shadow-xs shrink-0"
                        />
                      )}
                    </div>

                    {/* Meta info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <span className={`text-xs truncate ${isSelected || hasUnread ? 'font-bold text-foreground' : 'font-semibold text-foreground/90'}`}>
                          {title}
                        </span>
                        <span className="text-[10px] text-muted-foreground flex-shrink-0">
                          {formatTimestamp(conv.lastMessage?.createdAt || conv.createdAt)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-1">
                        <p className={`text-[11px] truncate leading-tight ${hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                          {getConversationSubtitle(conv)}
                        </p>
                        {hasUnread && (
                          <span className="px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground font-bold text-[10px] flex-shrink-0 shadow-xs">
                            {conv.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Matching workspace members when searching */}
              {membersWithoutDm.length > 0 && (
                <div className="pt-3">
                  <div className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Workspace Members
                  </div>
                  {membersWithoutDm.map((m) => (
                    <div
                      key={m.user.id}
                      onClick={() => handleStartDmWithMember(m)}
                      className="group relative p-2.5 rounded-xl border border-transparent hover:bg-muted/50 hover:border-border/60 transition cursor-pointer flex items-center justify-between gap-2"
                    >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <UserAvatar
                            userId={m.user.id}
                            displayName={m.user.displayName}
                            avatarUrl={m.user.avatarUrl}
                            size="sm"
                            className="shadow-2xs flex-shrink-0"
                          />
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-foreground truncate">
                              {m.user.displayName}
                            </p>
                            <span className="text-[10px] text-muted-foreground capitalize">
                              {m.role.toLowerCase()}
                            </span>
                          </div>
                        </div>

                        <span className="text-[11px] font-semibold text-primary group-hover:underline">
                          Message
                        </span>
                      </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </aside>

      {/* ====================================================================
          RIGHT COLUMN: Active 1-on-1 Conversation Chat Pane / Welcome Screen
          ==================================================================== */}
      <main className="flex-1 flex flex-col h-full bg-background min-w-0 overflow-hidden relative">
        {activeConversation ? (
          <>
            {/* Direct Conversation Header */}
            <header className="h-16 min-h-[64px] border-b border-border bg-card/40 px-5 flex items-center justify-between gap-4 select-none flex-shrink-0 z-10">
              <div className="flex items-center gap-3 min-w-0">
                <div className="relative flex-shrink-0">
                  {isGroupActive ? (
                    <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary border border-primary/30 flex items-center justify-center font-bold text-xs shadow-xs">
                      <Users className="h-5 w-5" />
                    </div>
                  ) : (
                    <UserAvatar
                      userId={firstOtherActive?.id}
                      displayName={activeTitle}
                      avatarUrl={firstOtherActive?.avatarUrl}
                      size="md"
                      showStatus
                      className="shadow-xs shrink-0"
                    />
                  )}
                </div>

                <div className="min-w-0">
                  <h3 className="font-bold text-foreground text-sm sm:text-base truncate">
                    {activeTitle}
                  </h3>
                  <p className="text-xs text-muted-foreground truncate flex items-center gap-1.5">
                    {isGroupActive ? (
                      <span>{t('dm.membersInGroup', { count: otherMembersActive.length + 1 })}</span>
                    ) : (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span>{t('dm.activeInWorkspace')}</span>
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {/* Emergency Alert Button */}
                <button
                  type="button"
                  onClick={openSendModal}
                  title={t('dm.emergencyAlert')}
                  aria-label={t('dm.emergencyAlert')}
                  className="px-2.5 py-1.5 rounded-xl border border-red-500/30 bg-red-500/10 text-red-500 hover:bg-red-500/20 transition shadow-xs flex items-center gap-1.5 font-semibold text-xs"
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{t('dm.emergencyAlert')}</span>
                </button>

                {/* Call Buttons for 1-on-1 */}
                {!isGroupActive && firstOtherActive && currentWorkspace && (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        initiateCall({
                          workspaceId: currentWorkspace.id,
                          conversationId: activeConversation.id,
                          recipientId: firstOtherActive.id,
                          recipientName: firstOtherActive.displayName,
                          recipientAvatar: firstOtherActive.avatarUrl ?? undefined,
                          callType: 'AUDIO'
                        });
                      }}
                      title={t('dm.voiceCall')}
                      aria-label={t('dm.voiceCall')}
                      className="p-2 rounded-xl border border-border bg-card hover:bg-muted text-muted-foreground hover:text-primary transition shadow-xs"
                    >
                      <Phone className="h-4 w-4" />
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        initiateCall({
                          workspaceId: currentWorkspace.id,
                          conversationId: activeConversation.id,
                          recipientId: firstOtherActive.id,
                          recipientName: firstOtherActive.displayName,
                          recipientAvatar: firstOtherActive.avatarUrl ?? undefined,
                          callType: 'VIDEO'
                        });
                      }}
                      title={t('dm.videoCall')}
                      aria-label={t('dm.videoCall')}
                      className="p-2 rounded-xl border border-border bg-card hover:bg-muted text-muted-foreground hover:text-primary transition shadow-xs"
                    >
                      <Video className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </header>

            {/* Messages Scroll Area */}
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 select-text"
            >
              {isLoadingMessages && messages.length === 0 ? (
                <div className="h-64 flex flex-col items-center justify-center gap-3 text-muted-foreground text-xs">
                  <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                  <span>Loading messages...</span>
                </div>
              ) : messages.length === 0 ? (
                <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-center p-8">
                  {isGroupActive ? (
                    <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary border border-primary/30 flex items-center justify-center font-bold text-lg mb-3 shadow-md">
                      <Users className="h-7 w-7" />
                    </div>
                  ) : (
                    <UserAvatar
                      userId={firstOtherActive?.id}
                      displayName={activeTitle}
                      avatarUrl={firstOtherActive?.avatarUrl}
                      size="xl"
                      className="mb-3 shadow-md shrink-0"
                    />
                  )}
                  <h4 className="text-base font-bold text-foreground mb-1">
                    {activeTitle}
                  </h4>
                  <p className="text-xs text-muted-foreground max-w-sm leading-relaxed">
                    This is the start of your direct message history with {activeTitle}. Send a message below to get started!
                  </p>
                </div>
              ) : (
                <>
                  {hasMoreMessages && (
                    <div className="flex justify-center py-2">
                      <button
                        type="button"
                        onClick={loadOlderMessages}
                        disabled={isLoadingMessages}
                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-muted hover:bg-muted/80 text-muted-foreground text-xs font-semibold rounded-full border border-border transition"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                        <span>{isLoadingMessages ? 'Loading older messages...' : 'Load older messages'}</span>
                      </button>
                    </div>
                  )}

                  {messages.map((message, index) => {
                    const prevMessage = index > 0 ? messages[index - 1] : null;
                    const { isFirstInGroup, isLastInGroup } = getGroupingInfo(index);

                    const showDateDivider =
                      !prevMessage ||
                      new Date(prevMessage.createdAt).toDateString() !==
                        new Date(message.createdAt).toDateString();

                    return (
                      <React.Fragment key={message.id}>
                        {showDateDivider && (
                          <div className="flex items-center my-4">
                            <div className="flex-1 border-t border-border/60" />
                            <span className="px-3 text-[11px] font-bold text-muted-foreground uppercase tracking-wider bg-background">
                              {new Date(message.createdAt).toLocaleDateString([], {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                            <div className="flex-1 border-t border-border/60" />
                          </div>
                        )}

                        <MessageItem
                          message={message}
                          isFirstInGroup={isFirstInGroup}
                          isLastInGroup={isLastInGroup}
                        />
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </div>

            {/* Message Composer */}
            <div className="p-4 border-t border-border bg-card/20 flex-shrink-0">
              <MessageComposer
                onSendMessage={sendMessage}
                placeholder={`Message ${activeTitle}...`}
              />
            </div>
          </>
        ) : (
          /* WhatsApp Web Style Welcome / Empty Screen */
          <div className="h-full flex flex-col items-center justify-center text-center p-8 bg-muted/5 select-none">
            <div className="w-20 h-20 rounded-3xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-5 text-primary shadow-sm">
              <MessageSquare className="h-10 w-10" />
            </div>

            <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2 tracking-tight">
              Sahaj Direct Messages
            </h3>

            <p className="text-xs sm:text-sm text-muted-foreground max-w-md leading-relaxed mb-6">
              Connect 1-on-1 with your team members, share files and code snippets, or initiate real-time audio and video calls.
            </p>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsNewDmModalOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground font-semibold text-xs sm:text-sm rounded-xl shadow-md hover:bg-primary/90 transition active:scale-95"
              >
                <Plus className="h-4 w-4" />
                <span>Start New Direct Message</span>
              </button>

              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-card border border-border hover:bg-muted text-foreground font-semibold text-xs sm:text-sm rounded-xl transition"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back to Channels</span>
                </button>
              )}
            </div>
          </div>
        )}
      </main>

      {/* ====================================================================
          New DM / Group DM Modal
          ==================================================================== */}
      {isNewDmModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">
            {/* Modal Header */}
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2.5 font-bold text-base text-foreground">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <UserPlus className="h-4 w-4" />
                </div>
                <span>New Direct Message</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsNewDmModalOpen(false);
                  setSelectedUserIds([]);
                  setGroupTitle('');
                }}
                className="p-1 rounded-lg hover:bg-muted text-muted-foreground transition"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleCreateDmModal} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-5 space-y-4 flex-1 overflow-y-auto">
                {/* Group Title (Only if > 1 selected) */}
                {selectedUserIds.length > 1 && (
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      {t('dm.groupName')}
                    </label>
                    <input
                      type="text"
                      placeholder={t('dm.groupNamePlaceholder')}
                      value={groupTitle}
                      onChange={(e) => setGroupTitle(e.target.value)}
                      className="w-full h-10 px-3.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                    />
                  </div>
                )}

                {/* Member Search */}
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    {t('dm.selectMembers')}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder={t('dm.searchMembersPlaceholder')}
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      className="w-full h-10 pl-9 pr-3.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                    />
                  </div>
                </div>

                {/* Member List */}
                <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                  {modalFilteredMembers.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-6">{t('dm.noMembersFound')}</p>
                  ) : (
                    modalFilteredMembers.map((m) => {
                      const isSelected = selectedUserIds.includes(m.user.id);

                      return (
                        <div
                          key={m.user.id}
                          onClick={() => handleToggleMemberModal(m.user.id)}
                          className={`flex items-center justify-between p-2.5 rounded-xl border transition cursor-pointer select-none ${
                            isSelected
                              ? 'bg-primary/10 border-primary/40 text-foreground ring-1 ring-primary/20'
                              : 'bg-card border-border/80 hover:bg-muted/50 text-foreground'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <UserAvatar
                              userId={m.user.id}
                              displayName={m.user.displayName}
                              avatarUrl={m.user.avatarUrl}
                              size="sm"
                              className="shadow-2xs flex-shrink-0"
                            />
                            <div>
                              <p className="text-xs font-semibold text-foreground">{m.user.displayName}</p>
                              <span className="text-[10px] text-muted-foreground capitalize">{m.role.toLowerCase()}</span>
                            </div>
                          </div>

                          <div
                            className={`h-5 w-5 rounded-md flex items-center justify-center border transition ${
                              isSelected
                                ? 'bg-primary border-primary text-primary-foreground'
                                : 'border-border bg-background'
                            }`}
                          >
                            {isSelected && <Check className="h-3.5 w-3.5 stroke-[3]" />}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-border bg-card/60 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {selectedUserIds.length === 0
                    ? t('dm.selectAtLeastOne')
                    : selectedUserIds.length === 1
                    ? t('dm.onePersonSelected')
                    : t('dm.membersSelectedGroup', { count: selectedUserIds.length })}
                </span>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsNewDmModalOpen(false)}
                    className="px-4 py-2 rounded-xl text-xs font-medium text-muted-foreground hover:bg-muted transition"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={selectedUserIds.length === 0 || isSubmitting}
                    className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition disabled:opacity-50 shadow-sm"
                  >
                    {isSubmitting
                      ? t('common.loading')
                      : selectedUserIds.length > 1
                      ? t('dm.startGroupDm')
                      : t('dm.startConversation')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
